import React, { useContext, useEffect, useState } from "react";
import CustomSnackbar from "../../../commanComponents/CustomerSnackbar";
import {
  Box,
  Container,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { getColor } from "../../../style/color/color";

import EstimatorCard from "../EstimatorCard";
import DashboardPieChart from "../DashboardPieChart";
import { Info } from "@mui/icons-material";
import { String } from "../../../constants/Strings";
import TotalAssetAmountCard from "./TotalAssetAmountCard";
import CryptoJS from "crypto-js";
import { useAgentDashboardService } from "../../../api/apiEndpoints/AgentFetch";
import SavingGoalCards from "./SavingGoalCards";
import { formatToIndianCurrency } from "../../../logic/CurrencyFormatter";
import { fonts } from "../../../style/fonts/fonts";
import { gaCustomEvent } from "../../../logic/CommonFunctions";
import { OTPVerifyContext } from "../../../Hooks/context/OTPVeriryContext";

const AgentDashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    totalAssets: "",
    assets: [],
    totalNetWorth: "",
    totalLiabilities: "",
    monthlySaving: "",
    monthlySalary: "",
    monthlyEmis: "",
    insuranceEstimator: {
      currentLifeCoverage: "",
      idealTermLifeInsurance: "",
      percentage: "",
    },
    goals: [],
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState({
    message: "",
    severity: "",
  });

  const { verifyData } = useContext(OTPVerifyContext);

  const SECRET_KEY = CryptoJS.enc.Utf8.parse("1234567890123456"); // 16-byte key
  const IV = CryptoJS.enc.Utf8.parse("abcdefghijklmnop");

  const URL = window.location.href;

  const decryptData = (encryptedData) => {
    try {
      if (!encryptedData)
        throw new Error(String.OnbordingStr.noDecryptedErrorMessage);

      // Decode Base64 encoded string
      const decodedData = CryptoJS.enc.Base64.parse(encryptedData);

      // Decrypt the data using AES
      const decrypted = CryptoJS.AES.decrypt(
        { ciphertext: decodedData },
        SECRET_KEY,
        {
          iv: IV,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        }
      );

      // Convert decrypted data to UTF-8 string
      const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
      if (!decryptedText)
        throw new Error(String.OnbordingStr.DecryptedErrorMessage);

      // Parse the JSON string if applicable
      return JSON.parse(decryptedText);
    } catch (error) {
      return null;
    }
  };
  const res = decryptData(URL.match(/view=(.+)/)[1]);
  const { customerId, token, customerName } = res;

  const { fetchDashboardDetails } = useAgentDashboardService();

  useEffect(() => {
    const fetchDashboardDetail = async () => {
      try {
        const response = await fetchDashboardDetails({
          body: {
            userId: customerId,
          },
          token,
        });

        if (response.httpCode === 200) {
          setDashboardData(response.response);
          setSnackbarMessage({
            message: String.Dashboard.mainDashboard.dashboardDataFetchSuccess,
            severity: String.successSeverity,
          });
          setSnackbarOpen(true);
        } else {
          setSnackbarMessage({
            message: String.Dashboard.expiredLink,
            severity: String.ErrorSeverity,
          });
          setSnackbarOpen(true);
        }
      } catch (error) {
        setSnackbarMessage({
          message: String.Dashboard.mainDashboard.dashboardDataFetchError,
          severity: String.ErrorSeverity,
        });
        setSnackbarOpen(true);
      }
    };
    fetchDashboardDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    gaCustomEvent(String.gaEventName.acrDashboard, String.gaEventClickName.acrDashboardHome, verifyData.customerId);
  }, [verifyData.customerId]);

  return (
    <Stack
      sx={{
        backgroundColor: getColor.SecondaryMidnightBlue,
        height: "100vh",
        overflowY: "aoto",
      }}
    >
      <Container sx={{}}>
        <Box
          sx={{
            display: "flex",
            mt: "40px",
          }}
        >
          <Typography
            sx={{
              color: getColor.PrimaryWhite,
              fontSize: "24px",
              fontFamily: fonts.manrope,
              fontWeight: 700,
              mt: "5px",
            }}
          >
            {String.Dashboard.mainDashboard.heading}
          </Typography>
        </Box>
      </Container>
      <Box sx={{ ml: "30px" }}>
        <Box
          sx={{
            display: "flex",

            justifyContent: "space-between",
            mt: "30px",
            pr: 2,
            pl: 1,
          }}
        >
          <Typography
            sx={{
              color: getColor.PrimaryWhite,
              fontSize: "18px",
              fontFamily: fonts.manrope,
            }}
          >{`${customerName}`}</Typography>
          <Typography
            sx={{
              color: getColor.PrimaryWhite,
              fontSize: "18px",
              fontFamily: fonts.manrope,
            }}
          >{`Custm ID:${customerId}`}</Typography>
        </Box>
        <Divider sx={{ mt: "20px", color: getColor.PrimaryWhite }} />
        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
          <Typography sx={{ fontSize: 12, color: getColor.PrimaryWhite }}>
            {String.Dashboard.mainDashboard.TotalAssets}
          </Typography>
          <Tooltip
            title={
              String.Dashboard.totolAMountCardTooltip.agentdashboardTooltip
            }
            sx={{ position: "relative", ml: "5px" }}
            enterTouchDelay={0}
          >
            <IconButton>
              <Info
                sx={{
                  width: 12,
                  height: 12,
                  fontSize: "1rem",
                  color: getColor.PrimaryWhite,
                  fontFamily: fonts.manrope,
                  fontWeight: "700",
                  left: 0,
                  top: 0,
                  position: "absolute",
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography
          sx={{
            color: getColor.PrimaryWhite,
            fontSize: 25,
            fontFamily: fonts.manrope,
            fontWeight: "700",
            mt: "5px",
          }}
        >
          {formatToIndianCurrency(dashboardData.totalAssets)}
        </Typography>
      </Box>
      {/* Pie Chart container */}
      <Box
        sx={{
          maxWidth: "100%",
          margin: "0 auto",
          backgroundColor: getColor.indigo,
          color: getColor.PrimaryWhite,
          p: 1,
          borderRadius: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          "@media (max-width: 600px)": {
            p: 1,
            borderRadius: 2,
          },
        }}
      >
        <DashboardPieChart assets={dashboardData.assets || []} />
      </Box>
      {/* this section conatin total net worth total libalities monthly monthly emi */}

      <>
        <Box
          sx={{
            backgroundColor: "#003A70",
            color: getColor.PrimaryWhite,
            p: 2,
            borderRadius: 4,
            display: "flex",
            flexDirection: "column",
            mb: 2,
          }}
        >
          <TotalAssetAmountCard
            monthlySaving={dashboardData.monthlySaving}
            monthlyIncome={dashboardData.monthlySalary}
          />
        </Box>
        <Typography
          sx={{
            color: getColor.PrimaryWhite,
            fontSize: 13,
            fontFamily: fonts.manrope,
            fontWeight: "500",
            mb: "30px",
            ml: "30px",
          }}
        >
          {String.Dashboard.mainDashboard.Alldataareindicative}
        </Typography>

        {/* // card where user details */}
        <Container
          sx={{
            background: "white",
            borderTopLeftRadius: 40,
            borderTopRightRadius: 40,
            overflowY: "aoto",
          }}
        >
          {" "}
          {/* // estimator card */}
          <EstimatorCard
            lifeCoverage={dashboardData.insuranceEstimator.currentLifeCoverage}
            termLifeInsurance={
              dashboardData.insuranceEstimator.idealTermLifeInsurance
            }
            percentage={dashboardData.insuranceEstimator.percentage}
          />
          {/* Savings Goals */}
          <SavingGoalCards goals={dashboardData.goals} />
        </Container>
      </>

      <CustomSnackbar
        open={snackbarOpen}
        handleClose={() => setSnackbarOpen(false)}
        message={snackbarMessage.message}
        severity={snackbarMessage.severity}
      />
    </Stack>
  );
};

export default AgentDashboard;
